import { useConnectionAction } from "./useConnectionAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { Label } from "@components/ui/label";
import { FormChoiceSelect } from "@components/ui/choice-select";
import { startIntercomOAuth, updateIntegrationConnection, type IntegrationConnection } from "../../lib/integrations";
import { FormField } from "./FormField";
import { intercomFormSchema, type IntercomForm } from "./integrationFormSchemas";

export function IntercomConnectionCard({ connection, onSaved, onCancel }: { connection?: IntegrationConnection; onSaved: () => Promise<void>; onCancel: () => void }) {
  const action = useConnectionAction();
  const isSubmitting = action.pending;
  const { register, control, handleSubmit, formState: { errors } } = useForm<IntercomForm>({
    resolver: zodResolver(intercomFormSchema),
    defaultValues: {
      name: connection?.name ?? "Intercom",
      region: (connection?.configSummary.region as IntercomForm["region"] | undefined) ?? "US",
    },
  });

  const authorize = async (values: IntercomForm) => {
    const { authorizationUrl } = await startIntercomOAuth({ ...values, ...(connection ? { connectionId: connection.id } : {}) });
    action.redirect(authorizationUrl);
  };

  const save = handleSubmit((values) => action.run(connection ? "save" : "authorize", async () => {
    try {
      if (!connection) return await authorize(values);
      await updateIntegrationConnection(connection.id, { name: values.name, config: { region: values.region } });
      toast.success("Intercom settings updated.");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to connect Intercom.");
    }
  }));

  const reconnect = handleSubmit((values) => action.run("authorize", async () => {
    try { await authorize(values); }
    catch (error) { toast.error(error instanceof Error ? error.message : "Unable to authorize connection."); }
  }));

  return (
    <form className="space-y-4" onSubmit={save} noValidate>
      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        Intercom is read-only in this first slice. IO Spoke imports a bounded set of conversations without changing the inbox.
      </div>
      <FormField id="intercom-name" label="Connection name" registration={register("name")} error={errors.name} />
      <div className="space-y-2">
        <Label htmlFor="intercom-region">Workspace data region</Label>
        <FormChoiceSelect id="intercom-region" control={control} name="region" options={[{ value: "US", label: "United States" }, { value: "EU", label: "Europe" }, { value: "AU", label: "Australia" }]} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={isSubmitting} loading={connection ? action.saving : action.authorizing} loadingLabel={connection ? "Saving…" : "Authorizing…"}>
          {connection ? "Save settings" : <> <ExternalLink className="mr-2 h-4 w-4" />Continue to Intercom</>}
        </Button>
        {connection ? (
          <Button type="button" variant="outline" onClick={reconnect} disabled={isSubmitting} loading={action.authorizing} loadingLabel="Authorizing…">
            Reconnect Intercom
          </Button>
        ) : null}
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
